import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getShipments, getUsers, getFlights, mapCustomer, deleteShipment, assignFlight, createAlias, updateLocation, getExpected, matchExpected, countCansShipment, updateCanCount, recountAll, getRecountStatus, cancelRecount, getShipmentImages, setDisposition, setWarehouse } from '../../api';
import { InlineCustomerSearch, CustomerSearch } from '../../components/CustomerSearch';
import InsideImagesModal from '../../components/InsideImagesModal';
import CanCountCell from '../../components/CanCountCell';
import ShipmentDrawer from '../../components/ShipmentDrawer';
import ImageViewer from '../../components/ImageViewer';

const FLIGHT_ELIGIBLE_LOCATIONS = ['Kho 1', 'Kho 4', 'Kho Surrey', 'Đã chuyển lên Kho 1'];
const canAssignToFlight = (sh) => FLIGHT_ELIGIBLE_LOCATIONS.includes(sh.current_location || sh.warehouse);

// Một shipment đang pending (chưa có flight) thuộc về đúng một tab. Unclaim ưu tiên hơn disposition
// — kiện chưa biết của ai phải xử lý trước, không quan tâm hold/discard.
const tabOf = (s) => {
  if (!s.customer_id) return 'unclaim';
  if (s.disposition === 'hold') return 'hold';
  if (s.disposition === 'discard') return 'discard';
  return 'pending';
};

const TABS = [
  { key: 'pending',  label: 'Pending',  icon: '📥', color: '#a16207' },
  { key: 'unclaim',  label: 'Unclaim',  icon: '📦', color: '#0284c7' },
  { key: 'hold',     label: 'Hold',     icon: '🔒', color: '#7c3aed' },
  { key: 'discard',  label: 'Discard',  icon: '✕',  color: '#dc2626' },
];


function CustomerFilterSearch({ customers, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (!value) setQuery(''); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = query
    ? customers.filter(c => c.code.toLowerCase().includes(query.toLowerCase()) || c.name.toLowerCase().includes(query.toLowerCase()))
    : customers.slice(0, 8);

  return (
    <div ref={ref} style={{position:'relative'}}>
      <div style={{position:'relative',display:'flex',alignItems:'center'}}>
        <input className="fs"
          value={query}
          onChange={e=>{ setQuery(e.target.value); setOpen(true); if(!e.target.value) onChange(''); }}
          onFocus={()=>setOpen(true)}
          placeholder="Search customer code..."
          style={{paddingRight: value ? 28 : 12, minWidth:190}}
        />
        {value && <button onMouseDown={()=>{ setQuery(''); setOpen(false); onChange(''); }}
          style={{position:'absolute',right:8,background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:16,lineHeight:1}}>×</button>}
      </div>
      {open && filtered.length > 0 && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,background:'var(--bg-secondary)',border:'1px solid rgba(15,23,42,0.10)',borderRadius:8,zIndex:999,maxHeight:220,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,0.5)',minWidth:220}}>
          {filtered.map(c=>(
            <div key={c.id} onMouseDown={()=>{ setQuery(`${c.code} — ${c.name}`); setOpen(false); onChange(c.id); }}
              style={{padding:'8px 12px',cursor:'pointer',fontSize:13,color:'var(--text-primary)',borderBottom:'1px solid rgba(15,23,42,0.05)',display:'flex',gap:10}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--border)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            >
              <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:700,color:'var(--accent)',fontSize:12}}>{c.code}</span>
              <span style={{color:'var(--text-secondary)'}}>{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ShipmentsPending() {
  const location = useLocation();
  const [shipments, setShipments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [flights, setFlights] = useState([]);
  const [filters, setFilters] = useState({
    warehouse: '',
    date_from: '',
    date_to: '',
    // Deep-link từ "By Customer": /shipments/pending?customer_id=123
    customer_id: new URLSearchParams(location.search).get('customer_id') || '',
    search: '',
  });
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(location.search).get('unmapped') === 'true' ? 'unclaim' : 'pending'
  );
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkFlight, setBulkFlight] = useState('');
  const [locationFilter, setLocationFilter] = useState(
    new URLSearchParams(location.search).get('location') || ''
  );
  const [unmatchedFilter, setUnmatchedFilter] = useState(false);
  const [duplicateFilter, setDuplicateFilter] = useState(false);
  const [canCountExact, setCanCountExact] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const [bulkWarehouse, setBulkWarehouse] = useState('');
  const [bulkCustomer, setBulkCustomer] = useState('');
  // Separate input for search so debounce stays responsive
  const [searchInput, setSearchInput] = useState('');
  const [aliasModal, setAliasModal] = useState(null);
  const [imageModal, setImageModal] = useState(null);
  const [insideModal, setInsideModal] = useState(null);
  const [editModal, setEditModal] = useState(null); // {id, tracking_code, customer_order, warehouse}
  const [editLoading, setEditLoading] = useState(false);
  const [matchModal, setMatchModal] = useState(null);
  const [expectedOptions, setExpectedOptions] = useState([]);
  const [matchSearch, setMatchSearch] = useState('');
  const [matchConfirm, setMatchConfirm] = useState(null); // {expectedId, code, isReceived}
  const [matchSuccess, setMatchSuccess] = useState(null); // code just matched
  const [countingIds, setCountingIds] = useState(new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [viewer, setViewer] = useState(null); // { images: string[], title: string }
  const [listKey, setListKey] = useState(0);

  const openLabelViewer = (url, tracking) => setViewer({ images: [url], title: tracking ? `Label · ${tracking}` : 'Label' });
  const openInsideViewer = async (id, tracking) => {
    try {
      const imgs = await getShipmentImages(id);
      if (!imgs.length) { alert('No inside photos'); return; }
      setViewer({ images: imgs.map(i => i.image_url), title: `Inside · ${tracking}` });
    } catch (e) { alert(e.message); }
  };
  const [recountActive, setRecountActive] = useState(false);
  const [recountStatus, setRecountStatus] = useState(null);

  // On mount: if a background job is running, restore the banner
  useEffect(() => {
    getRecountStatus().then(st => {
      if (st && !st.done && st.total > 0) {
        setRecountStatus(st);
        setRecountActive(true);
      }
    }).catch(() => {});
  }, []);

  // Poll job status while running
  useEffect(() => {
    if (!recountActive) return;
    let stopped = false;
    const poll = async () => {
      try {
        const st = await getRecountStatus();
        if (stopped) return;
        setRecountStatus(st);
        if (st.done) {
          setRecountActive(false);
          load();
        }
      } catch(e) { console.error(e); }
    };
    poll();
    const iv = setInterval(poll, 2000);
    return () => { stopped = true; clearInterval(iv); };
  }, [recountActive]);

  const load = async () => {
    setLoading(true);
    setSelected([]);
    try {
      const params = {
        no_flight: 'true', // only pending — 4 tabs (pending/unclaim/hold/discard) filter client-side
        ...(filters.warehouse ? { warehouse: filters.warehouse } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(filters.customer_id ? { customer_id: filters.customer_id } : {}),
        ...(filters.search ? { search: filters.search } : {}),
      };
      setShipments(await getShipments(params));
      setListKey(k => k + 1);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    getUsers({role:'customer'}).then(setCustomers).catch(console.error);
    getFlights().then(setFlights).catch(console.error);
  }, []);

  // Deep-link từ "By Customer" (/shipments/pending?customer_id=&location=).
  // Chạy mỗi lần điều hướng tới trang (theo location.key) — kể cả khi component
  // không remount sạch — và XOÁ các filter cũ để hiện đúng khách + kho được click,
  // không bị dính bộ lọc còn sót từ lần trước.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const cid = sp.get('customer_id') || '';
    const loc = sp.get('location') || '';
    const unmapped = sp.get('unmapped') === 'true';
    const tab = sp.get('tab');
    if (!cid && !loc && !unmapped && !tab) return;
    setFilters(f => ({ ...f, customer_id: cid, warehouse: '', date_from: '', date_to: '', search: '' }));
    setSearchInput('');
    setLocationFilter(loc);
    setUnmatchedFilter(false);
    setDuplicateFilter(false);
    setCanCountExact('');
    const validTab = ['pending', 'hold', 'discard'].includes(tab) ? tab : 'pending';
    setActiveTab(unmapped ? 'unclaim' : validTab);
  }, [location.key]);

  // Auto-apply: any filter change → reload (small debounce to batch updates)
  useEffect(() => {
    const t = setTimeout(() => load(), 150);
    return () => clearTimeout(t);
  }, [filters.warehouse, filters.customer_id, filters.search, filters.date_from, filters.date_to]);

  // Tracking/order search: 300ms debounce + apply only when ≥3 chars (or empty to clear)
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => {
        const next = searchInput.length >= 3 ? searchInput : '';
        return f.search === next ? f : { ...f, search: next };
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleMap = async (sh, customerId) => {
    await mapCustomer(sh.id, customerId || null);
    if (customerId && sh.receiver_name) setAliasModal({ name: sh.receiver_name, customerId });
    load();
  };

  const handleSaveAlias = async () => {
    if (!aliasModal) return;
    await createAlias({ name_on_label: aliasModal.name, customer_id: aliasModal.customerId });
    setAliasModal(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shipment?')) return;
    await deleteShipment(id); load();
  };

  const handleLocation = async (id, location) => {
    await updateLocation(id, location);
    load();
  };

  // Sửa kho chụp khi staff chọn nhầm lúc scan (vd hàng Surrey mang lên Kho 1 chụp).
  const handleWarehouse = async (id, warehouse) => {
    try { await setWarehouse([id], warehouse); load(); }
    catch (e) { alert(e.message || 'Failed'); }
  };

  const handleBulkWarehouse = async () => {
    if (!bulkWarehouse || !selected.length) return;
    if (!window.confirm(`Change warehouse of ${selected.length} shipments to "${bulkWarehouse}"?`)) return;
    try {
      await setWarehouse(selected, bulkWarehouse);
      setBulkWarehouse(''); setSelected([]); load();
    } catch (e) { alert(e.message || 'Failed'); }
  };

  // Find duplicate codes
  const duplicateIds = new Set();
  shipments.forEach(sh => {
    shipments.forEach(sh2 => {
      if (sh.id === sh2.id) return;
      const tc1 = sh.tracking_code?.trim(), tc2 = sh2.tracking_code?.trim();
      const co1 = sh.customer_order?.trim(), co2 = sh2.customer_order?.trim();
      if ((tc1 && tc1 === tc2) || (co1 && co1 === co2) || (tc1 && tc1 === co2) || (co1 && co1 === tc2)) {
        duplicateIds.add(sh.id);
      }
    });
  });

  const distinctCanCounts = [...new Set(shipments.filter(s => s.can_count != null).map(s => s.can_count))].sort((a,b)=>a-b);
  const matchCanCountFilter = (sh) => {
    if (canCountExact === '') return true;
    return sh.can_count === parseInt(canCountExact, 10);
  };
  // Lọc theo vị trí. "Kho 1" gộp luôn "Đã chuyển lên Kho 1" (cùng chỗ bay,
  // chỉ khác đã qua bước nhận hay chưa) → lọc 1 phát ra cả hai.
  const matchLocationFilter = (sh) => {
    if (!locationFilter) return true;
    const eff = sh.current_location || sh.warehouse;
    if (locationFilter === 'Kho 1') return eff === 'Kho 1' || eff === 'Đã chuyển lên Kho 1';
    return eff === locationFilter;
  };

  // Count per tab — computed across the full loaded set so tab badges stay accurate
  // even when other filters narrow the visible rows.
  const tabCounts = { pending: 0, unclaim: 0, hold: 0, discard: 0 };
  shipments.forEach(s => { tabCounts[tabOf(s)]++; });

  const filteredShipments = shipments.filter(sh =>
    tabOf(sh) === activeTab &&
    matchLocationFilter(sh) &&
    (!unmatchedFilter || !sh.expected_matched) &&
    (!duplicateFilter || duplicateIds.has(sh.id)) &&
    matchCanCountFilter(sh)
  );

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s,id]);
  const toggleAll = () => setSelected(selected.length === filteredShipments.length ? [] : filteredShipments.map(s=>s.id));

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setEditLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/shipments/${editModal.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('kdx_token')}` },
        body: JSON.stringify({ tracking_code: editModal.tracking_code, customer_order: editModal.customer_order, warehouse: editModal.warehouse })
      });
      setEditModal(null);
      load();
    } catch(e) { alert(e.message); }
    finally { setEditLoading(false); }
  };

  const openMatchModal = async (sh) => {
    if (!sh.customer_id) { alert('This shipment has no customer yet!'); return; }
    setMatchModal({
      shipmentId: sh.id,
      customerId: sh.customer_id,
      shipmentTracking: sh.tracking_code,
      shipmentReceiver: sh.receiver_name,
      shipmentOrder: sh.customer_order,
      shipmentImage: sh.image_url,
    });
    setMatchSearch('');
    setExpectedOptions([]);
    setMatchConfirm(null);
    setMatchSuccess(null);
    try {
      const data = await getExpected({ customer_id: sh.customer_id });
      setExpectedOptions(data);
    } catch(e) { console.error(e); }
  };

  const handleConfirmMatch = async () => {
    if (!matchConfirm || !matchModal) return;
    try {
      await matchExpected(matchConfirm.expectedId, matchModal.shipmentId);
      setMatchConfirm(null);
      setMatchSuccess(matchConfirm.code);
      setMatchSearch('');
      load();
    } catch(e) { alert(e.message); }
  };

  const handleBulkLocation = async () => {
    if (!bulkLocation || !selected.length) return;
    await Promise.all(selected.map(id => updateLocation(id, bulkLocation)));
    setBulkLocation(''); setSelected([]); load();
  };

  const handleBulkCustomer = async () => {
    if (!bulkCustomer || !selected.length) return;
    await Promise.all(selected.map(id => mapCustomer(id, bulkCustomer)));
    setBulkCustomer(''); setSelected([]); load();
  };

  const handleCount = async (id) => {
    setCountingIds(s => new Set([...s, id]));
    try {
      await countCansShipment(id);
      load();
    } catch(e) { alert('Count error: ' + e.message); }
    finally {
      setCountingIds(s => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const handleUpdateCount = async (id, val) => {
    const n = val === '' || val === null ? null : parseInt(val, 10);
    if (n !== null && (Number.isNaN(n) || n < 0)) { alert('Quantity must be a number ≥ 0'); return; }
    try {
      await updateCanCount(id, n);
      load();
    } catch(e) { alert(e.message); }
  };

  const handleBulkCount = async () => {
    if (!selected.length) return;
    const eligible = shipments.filter(s => selected.includes(s.id) && s.has_inside_image);
    if (!eligible.length) { alert('No selected shipment has an inside image'); return; }
    if (!window.confirm(`AI count for ${eligible.length} shipments? (~5-10s each)`)) return;
    setCountingIds(s => new Set([...s, ...eligible.map(e=>e.id)]));
    for (const sh of eligible) {
      try { await countCansShipment(sh.id); }
      catch(e) { console.error('count fail', sh.id, e); }
      finally {
        setCountingIds(s => { const n = new Set(s); n.delete(sh.id); return n; });
      }
    }
    load();
  };

  const handleBulkDisposition = async (disp) => {
    if (!selected.length) return;
    const labels = { pending: 'Pending', hold: 'Hold', discard: 'Discard' };
    if (!window.confirm(`Move ${selected.length} shipments to "${labels[disp]}"?`)) return;
    try {
      await setDisposition(selected, disp);
      setSelected([]); load();
    } catch (e) { alert(e.message || 'Failed'); }
  };

  const handleBulkAssign = async () => {
    if (!bulkFlight || !selected.length) return;
    // Check if all selected have customer
    const noCustomer = shipments.filter(s => selected.includes(s.id) && !s.customer_id);
    if (noCustomer.length > 0) {
      alert(`⚠️ ${noCustomer.length} shipments not yet mapped to a customer!\nPlease map the customer before assigning a flight.`);
      return;
    }
    // Check eligible location (Wh 1 / Wh 4 / Transferred to Wh 1)
    const ineligible = shipments.filter(s => selected.includes(s.id) && !canAssignToFlight(s));
    if (ineligible.length > 0) {
      const sample = ineligible.slice(0,3).map(s=>`${s.tracking_code} (${s.current_location||s.warehouse})`).join(', ');
      alert(`⚠️ ${ineligible.length} shipments are at Warehouse 2/3 — move them to Warehouse 1 first.\nExample: ${sample}`);
      return;
    }
    try {
      await assignFlight(bulkFlight, selected);
      setBulkFlight(''); setSelected([]); load();
    } catch (e) {
      alert(e.message || 'Failed to assign flight');
    }
  };

  const totalCans = filteredShipments.reduce((sum,s)=>sum+(s.can_count||0),0);
  const eligibleForCount = filteredShipments.filter(s => s.has_inside_image && (s.can_count == null || s.can_count_status === 'error'));

  const handleCountFiltered = async () => {
    if (!eligibleForCount.length) return;
    if (!window.confirm(`AI count for ${eligibleForCount.length} filtered shipments? (~5-10s per shipment, cancellable)`)) return;
    try {
      await recountAll({ shipment_ids: eligibleForCount.map(s=>s.id) });
      setRecountActive(true);
    } catch(e) {
      if (/đang chạy/i.test(e.message)) alert('Another count job is running — wait and retry');
      else alert(e.message);
    }
  };

  const handleCancelRecount = async () => {
    try { await cancelRecount(); }
    catch(e) { alert(e.message); }
  };

  return (
    <div>
      <style>{`
        .fs,.fi{background:var(--bg-tertiary);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 12px;font-size:12.5px;color:var(--text-primary);font-family:var(--font-sans);transition:border-color var(--transition-fast);outline:none;}
        .fs:focus,.fi:focus{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-bg);}
        .fb{background:var(--bg-tertiary);color:var(--text-primary);border:1px solid var(--accent-strong);border-radius:8px;padding:8px 18px;font-size:12.5px;font-weight:600;cursor:pointer;font-family:var(--font-sans);transition:all 0.18s;}
        .fb:hover:not(:disabled){background:var(--bg-elevated);border-color:var(--accent);}
        .fb:disabled{opacity:0.45;cursor:not-allowed;}
        .ship-row:hover td{background:rgba(15,23,42,0.025);}
        .del-btn{background:transparent;border:1px solid rgba(220,38,38,0.20);color:#b91c1c;border-radius:var(--radius-sm);padding:4px 9px;font-size:11px;cursor:pointer;transition:all var(--transition-fast);}
        .del-btn:hover{background:rgba(220,38,38,0.08);border-color:rgba(220,38,38,0.35);}
        .cb{width:14px;height:14px;cursor:pointer;accent-color:var(--accent);}
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.75);display:flex;align-items:center;justify-content:center;z-index:100;}
        .modal{background:var(--bg-secondary);border:1px solid var(--border-strong);border-radius:var(--radius-xl);padding:24px;width:400px;}
        .toggle-btn{border-radius:var(--radius-md);padding:8px 14px;font-size:12.5px;font-weight:600;cursor:pointer;transition:all var(--transition-fast);font-family:var(--font-sans);}
        .toggle-btn.on{background:rgba(217,119,6,0.10);color:var(--color-warning);border:1px solid rgba(217,119,6,0.25);}
        .toggle-btn.off{background:var(--bg-tertiary);color:var(--text-muted);border:1px solid var(--border);}
        .toggle-btn:hover{border-color:rgba(217,119,6,0.30);color:var(--color-warning);}
      `}</style>

      <div style={s.topbar}>
        <div>
          <div className="page-eyebrow">MANAGEMENT</div><h1 className="page-title">Pending Shipments</h1>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          {totalCans > 0 && (
            <div style={s.canBadge}>
              <span style={s.canLabel}>TOTAL COUNT</span>
              <span style={s.canVal}>{totalCans}</span>
            </div>
          )}
          {eligibleForCount.length > 0 && !recountActive && (
            <button onClick={handleCountFiltered}
              style={{background:'rgba(124,58,237,0.12)',border:'1px solid rgba(124,58,237,0.35)',color:'var(--color-purple)',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',}}>
              🤖 AI Count ({eligibleForCount.length})
            </button>
          )}
          <div style={s.badge}>{filteredShipments.length} shipments</div>
        </div>
      </div>

      {recountActive && recountStatus && !recountStatus.done && (
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',marginBottom:14,background:'rgba(124,58,237,0.06)',border:'1px solid rgba(124,58,237,0.22)',borderRadius:8}}>
          <div style={{flex:1}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
              <span style={{fontSize:12,fontWeight:600,color:'var(--color-purple)'}}>🤖 AI counting: {recountStatus.processed}/{recountStatus.total}</span>
              <span style={{fontSize:11,color:'var(--text-muted)'}}>
                ✅ {recountStatus.success} · ⚠ {recountStatus.errors} · ETA ~{Math.max(1, Math.ceil((recountStatus.total - recountStatus.processed) * (recountStatus.throttleMs+5000) / 1000 / 60))} min
              </span>
            </div>
            <div style={{height:5,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
              <div style={{height:'100%',width:`${recountStatus.total>0?(recountStatus.processed/recountStatus.total*100):0}%`,background:'var(--color-purple)',transition:'width 0.5s'}} />
            </div>
          </div>
          <button onClick={handleCancelRecount}
            style={{background:'rgba(220,38,38,0.10)',color:'var(--color-danger)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:6,padding:'5px 11px',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
            ⏹ Stop
          </button>
        </div>
      )}

      {/* Tab strip — 4 trạng thái của pending shipments */}
      <div style={{display:'flex',gap:4,marginBottom:14,borderBottom:'1px solid var(--border-strong)'}}>
        {TABS.map(tab => {
          const active = activeTab === tab.key;
          const n = tabCounts[tab.key];
          return (
            <button key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSelected([]); }}
              style={{
                background: active ? `${tab.color}12` : 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? tab.color : 'transparent'}`,
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                color: active ? tab.color : 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s',
                marginBottom: -1,
              }}>
              <span>{tab.icon} {tab.label}</span>
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                background: active ? `${tab.color}22` : 'var(--border)',
                color: active ? tab.color : 'var(--text-muted)',
                padding: '2px 8px',
                borderRadius: 20,
                minWidth: 18,
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Filters — auto-apply on change, no Filter button */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16,alignItems:'center'}}>
        {/* Group 1: dropdown applies on select */}
        <select className="fs" value={filters.warehouse} onChange={e=>setFilters(f=>({...f,warehouse:e.target.value}))}>
          <option value="">Warehouse</option>
          <option value="Kho 1">Kho 1</option>
          <option value="Kho 2">Kho 2</option>
          <option value="Kho 3">Kho 3</option>
          <option value="Kho 4">Kho 4</option>
          <option value="Kho Surrey">Kho Surrey</option>
          <option value="ARBOURRIDGE">ARBOURRIDGE</option>
        </select>
        <select className="fs" value={locationFilter} onChange={e=>setLocationFilter(e.target.value)}>
          <option value="">Status</option>
          <option value="Kho 1">At Warehouse 1 (incl. transferred)</option>
          <option value="Kho 2">At Warehouse 2</option>
          <option value="Kho 3">At Warehouse 3</option>
          <option value="Kho 4">At Warehouse 4</option>
          <option value="Kho Surrey">At Surrey Warehouse</option>
          <option value="ARBOURRIDGE">At ARBOURRIDGE</option>
        </select>
        <select className="fs" value={canCountExact} onChange={e=>setCanCountExact(e.target.value)}>
          <option value="">Count</option>
          {distinctCanCounts.map(n => <option key={n} value={n}>{n}</option>)}
        </select>

        {/* Group 2: toggle on/off */}
        <button
          className={`toggle-btn ${unmatchedFilter ? 'on' : 'off'}`}
          onClick={() => setUnmatchedFilter(f=>!f)}
          style={{whiteSpace:'nowrap'}}
        >
          {unmatchedFilter ? '⚠ Not matched' : 'Not matched'}
        </button>
        <button
          className={`toggle-btn ${duplicateFilter ? 'on' : 'off'}`}
          onClick={() => setDuplicateFilter(f=>!f)}
          style={{whiteSpace:'nowrap'}}
        >
          {duplicateFilter ? '🔁 Duplicates' : 'Duplicates'}
        </button>

        {/* Group 3: search auto-apply (300ms debounce, ≥3 chars) */}
        <input
          className="fi"
          style={{minWidth:240}}
          placeholder="Type 3+ chars or full code to search tracking/order..."
          value={searchInput}
          onChange={e=>setSearchInput(e.target.value)}
        />
        <CustomerFilterSearch
          customers={customers}
          value={filters.customer_id}
          onChange={v => setFilters(f=>({...f, customer_id: v}))}
        />

        {/* Group 4: date */}
        <input className="fi" type="date" value={filters.date_from} onChange={e=>setFilters(f=>({...f,date_from:e.target.value}))} />
        <input className="fi" type="date" value={filters.date_to} onChange={e=>setFilters(f=>({...f,date_to:e.target.value}))} />
      </div>

      {/* Bulk assign */}
      {selected.length > 0 && (() => {
        const selectedIneligibleCount = shipments.filter(s => selected.includes(s.id) && !canAssignToFlight(s)).length;
        const showAssignFlight = activeTab === 'pending' || activeTab === 'hold';
        // Đổi vị trí kho chỉ có nghĩa khi kiện còn đang xử lý — ẩn ở Hold/Discard.
        const showChangeLocation = activeTab === 'pending' || activeTab === 'unclaim';
        const showAssignCustomer = activeTab === 'pending' || activeTab === 'unclaim';
        const showMoveToHoldDiscard = activeTab === 'pending';
        const showMoveBackToPending = activeTab === 'hold' || activeTab === 'discard';
        const sep = <div style={{width:1,height:24,background:'var(--border-strong)'}} />;
        return (
        <div style={{padding:'12px 14px',background:'var(--accent-bg)',border:'1px solid var(--accent-strong)',borderRadius:8,marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <span style={{fontSize:13,color:'var(--accent)',fontWeight:700}}>{selected.length} shipments selected</span>
            {showAssignFlight && selectedIneligibleCount > 0 && (
              <span style={{fontSize:11,color:'var(--color-warning)',background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.22)',borderRadius:4,padding:'2px 8px'}}>
                ⚠ {selectedIneligibleCount} shipments at Wh 2/3 — cannot assign flight
              </span>
            )}
            <button onClick={()=>setSelected([])} style={{background:'transparent',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:12,marginLeft:'auto'}}>× Deselect</button>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            {showAssignFlight && (<>
              <select className="fs" style={{margin:0}} value={bulkFlight} onChange={e=>setBulkFlight(e.target.value)}>
                <option value="">✈ Assign flight...</option>
                {flights.filter(f=>f.status==='pending').map(f=><option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <button className="fb" style={{padding:'6px 14px',fontSize:12,whiteSpace:'nowrap'}} onClick={handleBulkAssign} disabled={!bulkFlight}>
                ✈ Assign
              </button>
              {sep}
            </>)}
            {/* Change location — only where relocation is actionable (Pending/Unclaim) */}
            {showChangeLocation && (<>
            <select className="fs" style={{margin:0}} value={bulkLocation} onChange={e=>setBulkLocation(e.target.value)}>
              <option value="">📦 Change status...</option>
              <option value="Kho 1">At Warehouse 1</option>
              <option value="Kho 2">At Warehouse 2</option>
              <option value="Kho 3">At Warehouse 3</option>
              <option value="Kho 4">At Warehouse 4</option>
              <option value="Kho Surrey">At Surrey Warehouse</option>
              <option value="ARBOURRIDGE">At ARBOURRIDGE</option>
              <option value="Đã chuyển lên Kho 1">Transferred to Wh 1</option>
            </select>
            <button className="fb" style={{padding:'6px 14px',fontSize:12,whiteSpace:'nowrap'}} onClick={handleBulkLocation} disabled={!bulkLocation}>
              📦 Apply
            </button>
            </>)}
            {/* Sửa kho chụp — mọi tab, vì chọn nhầm kho có thể nằm ở bất kỳ đâu */}
            {sep}
            <select className="fs" style={{margin:0}} value={bulkWarehouse} onChange={e=>setBulkWarehouse(e.target.value)}>
              <option value="">🏬 Change warehouse...</option>
              <option value="Kho 1">Kho 1</option>
              <option value="Kho 2">Kho 2</option>
              <option value="Kho 3">Kho 3</option>
              <option value="Kho 4">Kho 4</option>
              <option value="Kho Surrey">Kho Surrey</option>
              <option value="ARBOURRIDGE">ARBOURRIDGE</option>
            </select>
            <button className="fb" style={{padding:'6px 14px',fontSize:12,whiteSpace:'nowrap'}} onClick={handleBulkWarehouse} disabled={!bulkWarehouse}>
              🏬 Apply
            </button>
            {showAssignCustomer && (<>
              {sep}
              <CustomerSearch
                customers={customers}
                value={bulkCustomer}
                onChange={setBulkCustomer}
                placeholder="👤 Assign customer..."
              />
              <button className="fb" style={{padding:'6px 14px',fontSize:12,whiteSpace:'nowrap'}} onClick={handleBulkCustomer} disabled={!bulkCustomer}>
                👤 Assign
              </button>
            </>)}
            {showMoveToHoldDiscard && (<>
              {sep}
              <button onClick={()=>handleBulkDisposition('hold')}
                style={{background:'rgba(124,58,237,0.12)',border:'1px solid rgba(124,58,237,0.30)',color:'var(--color-purple)',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                🔒 Hold
              </button>
              <button onClick={()=>handleBulkDisposition('discard')}
                style={{background:'rgba(220,38,38,0.10)',border:'1px solid rgba(220,38,38,0.30)',color:'var(--color-danger)',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                ✕ Discard
              </button>
            </>)}
            {showMoveBackToPending && (<>
              <button onClick={()=>handleBulkDisposition('pending')}
                style={{background:'rgba(22,163,74,0.10)',border:'1px solid rgba(22,163,74,0.30)',color:'var(--color-success)',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                ↩ Back to Pending
              </button>
            </>)}
            {sep}
            {/* Bulk AI count — all tabs */}
            <button onClick={handleBulkCount}
              style={{background:'rgba(124,58,237,0.12)',border:'1px solid rgba(124,58,237,0.30)',color:'var(--color-purple)',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',}}>
              🤖 AI Count ({selected.filter(id=>shipments.find(s=>s.id===id)?.has_inside_image).length})
            </button>
          </div>
        </div>
        );
      })()}

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={{width:'100%',borderCollapse:'collapse',position:'relative'}}>
          <thead>
            <tr>
              <th style={s.th}><input type="checkbox" className="cb" onChange={toggleAll} checked={selected.length===filteredShipments.length&&filteredShipments.length>0} /></th>
              {['Time','Tracking','Cust. Order','Recipient','Count','Warehouse','Status','Customer','Match'].map(h=>(
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody key={listKey}>
            {loading ? (
              <tr><td colSpan={10} style={s.empty}><div style={s.emptyInner}><span style={{opacity:0.3}}>Loading...</span></div></td></tr>
            ) : filteredShipments.length === 0 ? (
              <tr><td colSpan={10} style={s.empty}>
                <div style={s.emptyInner}>
                  <div style={{fontSize:26,marginBottom:8,opacity:0.2}}>{TABS.find(t=>t.key===activeTab)?.icon || '◈'}</div>
                  <div style={{fontWeight:600,color:'var(--text-muted)'}}>
                    No shipments in "{TABS.find(t=>t.key===activeTab)?.label || activeTab}"
                  </div>
                </div>
              </td></tr>
            ) : filteredShipments.map((sh, i)=>(
              <tr key={sh.id} className="ship-row row-animate"
                  onClick={()=>setDrawerId(sh.id)}
                  style={{borderBottom:'1px solid rgba(15,23,42,0.05)',cursor:'pointer',animationDelay:`${Math.min(i,12)*25}ms`}}>
                <td style={s.td} onClick={e=>e.stopPropagation()}>
                  <input type="checkbox" className="cb" checked={selected.includes(sh.id)} onChange={()=>toggleSelect(sh.id)} />
                </td>
                <td style={{...s.td,color:'var(--text-muted)',fontSize:11,whiteSpace:'nowrap'}}>{new Date(sh.scanned_at).toLocaleString('vi-VN')}</td>
                <td style={{...s.td,fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:11,color:'var(--text-muted)'}}>{sh.tracking_code}</td>
                <td style={{...s.td,fontVariantNumeric:"tabular-nums",fontWeight:600,color:'var(--accent)',fontSize:12}}>{sh.customer_order||'—'}</td>
                <td style={s.td}>{sh.receiver_name}</td>
                <td style={s.td} onClick={e=>e.stopPropagation()}>
                  <CanCountCell sh={sh} busy={countingIds.has(sh.id)} onCount={handleCount} onUpdate={handleUpdateCount} />
                </td>
                <td style={{...s.td,whiteSpace:'nowrap'}} onClick={e=>e.stopPropagation()}>
                  <select
                    value={sh.warehouse || ''}
                    onChange={e => handleWarehouse(sh.id, e.target.value)}
                    style={{
                      background:'var(--bg-secondary)', border:'1px solid var(--border-strong)',
                      borderRadius:6, padding:'5px 8px', fontSize:11, color:'var(--text-secondary)',
                      outline:'none', cursor:'pointer', maxWidth:120,
                    }}
                  >
                    {!sh.warehouse && <option value="">—</option>}
                    <option value="Kho 1">Kho 1</option>
                    <option value="Kho 2">Kho 2</option>
                    <option value="Kho 3">Kho 3</option>
                    <option value="Kho 4">Kho 4</option>
                    <option value="Kho Surrey">Kho Surrey</option>
                    <option value="ARBOURRIDGE">ARBOURRIDGE</option>
                  </select>
                </td>
                <td style={{...s.td,whiteSpace:'nowrap'}} onClick={e=>e.stopPropagation()}>
                  <select
                    value={sh.current_location || sh.warehouse || ''}
                    onChange={e => handleLocation(sh.id, e.target.value)}
                    style={{
                      background: sh.current_location === 'Đã chuyển lên Kho 1'
                        ? 'rgba(22,163,74,0.08)' : 'var(--bg-secondary)',
                      border: `1px solid ${sh.current_location === 'Đã chuyển lên Kho 1'
                        ? 'rgba(22,163,74,0.30)' : 'var(--border-strong)'}`,
                      borderRadius:6, padding:'5px 8px', fontSize:11,
                      color: sh.current_location === 'Đã chuyển lên Kho 1'
                        ? 'var(--color-success)' : 'var(--text-secondary)',
                       outline:'none', cursor:'pointer', maxWidth:160,
                    }}
                  >
                    <option value="Kho 1">At Warehouse 1</option>
                    <option value="Kho 2">At Warehouse 2</option>
                    <option value="Kho 3">At Warehouse 3</option>
                    <option value="Kho 4">At Warehouse 4</option>
                    <option value="Kho Surrey">At Surrey Warehouse</option>
                    <option value="ARBOURRIDGE">At ARBOURRIDGE</option>
                    <option value="Đã chuyển lên Kho 1">Transferred to Wh 1</option>
                  </select>
                </td>
                <td style={s.td} onClick={e=>e.stopPropagation()}>
                  <InlineCustomerSearch
                    customers={customers}
                    currentId={sh.customer_id}
                    currentName={sh.customer_id ? `${sh.customer_code} — ${sh.customer_name}` : ''}
                    onSelect={v => handleMap(sh, v)}
                  />
                </td>
                <td style={{...s.td,fontSize:11}} onClick={e=>e.stopPropagation()}>
                  <div style={{display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                    <span style={{color:sh.expected_matched?'var(--color-success)':'var(--color-warning)',fontWeight:600}}>
                      {sh.expected_matched?'✅ Matched':'⚠ Not matched'}
                    </span>
                    <button onClick={()=>openMatchModal(sh)}
                      style={{background:'rgba(37,99,235,0.10)',border:'1px solid rgba(37,99,235,0.22)',borderRadius:4,padding:'2px 6px',fontSize:10,color:'var(--color-info)',cursor:'pointer'}}>
                      🔗
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editModal && (
        <div className="modal-overlay" onClick={()=>setEditModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{width:440}}>
            <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',marginBottom:16}}>✏️ Edit shipment info</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Tracking Code</div>
              <input value={editModal.tracking_code} onChange={e=>setEditModal(m=>({...m,tracking_code:e.target.value}))}
                style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box'}}
                onFocus={e=>{e.target.style.borderColor='var(--accent)';e.target.style.boxShadow='0 0 0 3px var(--accent-bg)';}}
                onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none';}}
              />
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Customer Order</div>
              <input value={editModal.customer_order} onChange={e=>setEditModal(m=>({...m,customer_order:e.target.value}))}
                style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box'}}
                onFocus={e=>{e.target.style.borderColor='var(--accent)';e.target.style.boxShadow='0 0 0 3px var(--accent-bg)';}}
                onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none';}}
              />
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Warehouse</div>
              <select value={editModal.warehouse} onChange={e=>setEditModal(m=>({...m,warehouse:e.target.value}))}
                style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box'}}
                onFocus={e=>{e.target.style.borderColor='var(--accent)';e.target.style.boxShadow='0 0 0 3px var(--accent-bg)';}}
                onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none';}}>
                <option value="Kho 1">Kho 1</option>
                <option value="Kho 2">Kho 2</option>
                <option value="Kho 3">Kho 3</option>
                <option value="Kho 4">Kho 4</option>
                <option value="Kho Surrey">Kho Surrey</option>
                <option value="ARBOURRIDGE">ARBOURRIDGE</option>
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={handleSaveEdit} disabled={editLoading}
                style={{flex:1,background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'1px solid var(--accent-strong)',borderRadius:8,padding:'10px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                {editLoading?'Saving...':'✓ Save'}
              </button>
              <button onClick={()=>setEditModal(null)}
                style={{flex:1,background:'var(--border)',color:'var(--text-secondary)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:8,padding:'10px',fontSize:13,cursor:'pointer'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {matchModal && (
        <div className="modal-overlay" onClick={()=>{ setMatchModal(null); setMatchConfirm(null); setMatchSuccess(null); }}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{width:480}}>

            {/* SUCCESS STATE */}
            {matchSuccess ? (
              <div style={{textAlign:'center',padding:'12px 0'}}>
                <div style={{fontSize:32,marginBottom:12}}>✅</div>
                <div style={{fontSize:15,fontWeight:700,color:'var(--color-success)',marginBottom:6}}>Assigned successfully!</div>
                <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:20}}>
                  Code <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,color:'var(--color-info)',fontWeight:700}}>{matchSuccess}</span> was assigned to this shipment.
                </div>
                <button onClick={()=>{ setMatchModal(null); setMatchSuccess(null); }}
                  style={{background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'1px solid var(--accent-strong)',borderRadius:8,padding:'10px 28px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  Close
                </button>
              </div>

            /* CONFIRM STATE */
            ) : matchConfirm ? (
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',marginBottom:12}}>Confirm code assignment</div>
                <div style={{padding:'14px',background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:10,marginBottom:14}}>
                  <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:15,fontWeight:700,color:'var(--color-info)',marginBottom:6}}>{matchConfirm.code}</div>
                  {matchConfirm.isReceived ? (
                    <div style={{fontSize:12,color:'var(--color-warning)',lineHeight:1.6}}>
                      ⚠ This code is currently assigned to another shipment.<br/>
                      Confirming will move the code to this shipment and unassign from the old one.
                    </div>
                  ) : (
                    <div style={{fontSize:12,color:'var(--text-secondary)'}}>
                      Assign this code to the current shipment?
                    </div>
                  )}
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={handleConfirmMatch}
                    style={{flex:1,background:'var(--bg-tertiary)',color:matchConfirm.isReceived?'var(--color-warning)':'var(--text-primary)',border:`1px solid ${matchConfirm.isReceived?'#a16207':'var(--accent-strong)'}`,borderRadius:8,padding:'10px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                    ✓ Confirm assign
                  </button>
                  <button onClick={()=>setMatchConfirm(null)}
                    style={{flex:1,background:'var(--border)',color:'var(--text-secondary)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:8,padding:'10px',fontSize:13,cursor:'pointer'}}>
                    Cancel
                  </button>
                </div>
              </div>

            /* SEARCH STATE */
            ) : (
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',marginBottom:4}}>Manual code match</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:12}}>
                  For shipment: <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,color:'var(--accent)',fontWeight:700}}>{matchModal.shipmentTracking}</span>
                  {matchModal.shipmentReceiver ? ` · ${matchModal.shipmentReceiver}` : ''}
                </div>
                <input
                  placeholder="Type a few digits to find a code..."
                  value={matchSearch}
                  onChange={e=>setMatchSearch(e.target.value)}
                  autoFocus
                  style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box',marginBottom:12}}
                  onFocus={e=>{e.target.style.borderColor='var(--accent)';e.target.style.boxShadow='0 0 0 3px var(--accent-bg)';}}
                  onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none';}}
                />
                <div style={{maxHeight:'45vh',overflowY:'auto'}}>
                  {!matchSearch.trim() ? (
                    <div style={{padding:'24px',textAlign:'center',color:'var(--text-muted)',fontSize:13}}>
                      Type to search codes
                    </div>
                  ) : (() => {
                    const filtered = expectedOptions.filter(e =>
                      e.code.toLowerCase().includes(matchSearch.toLowerCase())
                    );
                    if (filtered.length === 0) return (
                      <div style={{padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:13}}>
                        No matching codes found
                      </div>
                    );
                    return filtered.map(e => {
                      const hasImage = !!e.image_url;
                      return (
                        <div key={e.id}
                          style={{display:'flex',gap:10,alignItems:'center',padding:'10px 12px',marginBottom:6,
                            background: e.status==='received'?'rgba(217,119,6,0.06)':'var(--bg-tertiary)',
                            border:`1px solid ${e.status==='received'?'rgba(217,119,6,0.15)':'var(--border)'}`,
                            borderRadius:8}}>
                          {hasImage ? (
                            <img src={e.image_url} alt="label" onClick={()=>setImageModal(e.image_url)}
                              style={{width:44,height:44,objectFit:'cover',borderRadius:6,border:'1px solid rgba(22,163,74,0.30)',flexShrink:0,cursor:'zoom-in'}} />
                          ) : (
                            <div style={{width:44,height:44,borderRadius:6,background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.06)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,opacity:0.2,flexShrink:0}}>📷</div>
                          )}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:13,fontWeight:700,color:'var(--color-info)',marginBottom:3,wordBreak:'break-all'}}>{e.code}</div>
                            {e.tracking_code && (
                              <div style={{fontSize:10,color:'var(--text-muted)',fontVariantNumeric:"tabular-nums",fontWeight:600,marginBottom:4}}>↳ Shipment: {e.tracking_code}</div>
                            )}
                            <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                              <span style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:20,
                                color: e.status==='received'?'var(--color-warning)':'var(--text-muted)',
                                background: e.status==='received'?'rgba(217,119,6,0.10)':'var(--border)',
                                border: `1px solid ${e.status==='received'?'rgba(217,119,6,0.20)':'var(--border)'}`}}>
                                {e.status==='received'?'⚠ Assigned to another shipment':'⏳ Not arrived'}
                              </span>
                              {e.shipment_id && (
                                <button onClick={()=>setInsideModal({id:e.shipment_id,tracking:e.tracking_code||e.code})}
                                  style={{background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.20)',borderRadius:20,padding:'1px 8px',fontSize:10,fontWeight:600,color:'var(--color-warning)',cursor:'pointer'}}>
                                  📦 Inside
                                </button>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={()=>setMatchConfirm({ expectedId: e.id, code: e.code, isReceived: e.status==='received' })}
                            style={{background:'rgba(37,99,235,0.15)',border:'1px solid rgba(37,99,235,0.25)',borderRadius:6,padding:'5px 14px',fontSize:12,fontWeight:600,color:'var(--color-info)',cursor:'pointer',flexShrink:0}}>
                            Assign
                          </button>
                        </div>
                      );
                    });
                  })()}
                </div>
                <button onClick={()=>setMatchModal(null)} style={{width:'100%',marginTop:12,background:'var(--border)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:8,padding:'9px',fontSize:13,color:'var(--text-secondary)',cursor:'pointer'}}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {insideModal && (
        <InsideImagesModal
          shipmentId={insideModal.id}
          tracking={insideModal.tracking}
          onClose={()=>setInsideModal(null)}
        />
      )}

      {viewer && (
        <ImageViewer images={viewer.images} title={viewer.title} onClose={()=>setViewer(null)} />
      )}
      {imageModal && (
        <ImageViewer src={imageModal} title="Label" onClose={()=>setImageModal(null)} />
      )}

      <ShipmentDrawer
        shipment={drawerId ? shipments.find(s => s.id === drawerId) : null}
        customers={customers}
        onClose={()=>setDrawerId(null)}
        onLocationChange={handleLocation}
        onCustomerChange={handleMap}
        onEdit={(sh)=>setEditModal({id:sh.id,tracking_code:sh.tracking_code||'',customer_order:sh.customer_order||'',warehouse:sh.warehouse||''})}
        onDelete={async (id)=>{ await handleDelete(id); setDrawerId(null); }}
        onOpenLabel={(url)=>openLabelViewer(url, drawerId ? shipments.find(s=>s.id===drawerId)?.tracking_code : null)}
        onOpenInside={openInsideViewer}
        onMatch={openMatchModal}
        onCount={handleCount}
        onUpdateCount={handleUpdateCount}
        countingIds={countingIds}
        mode="pending"
      />

      {aliasModal && (
        <div className="modal-overlay" onClick={()=>setAliasModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',marginBottom:8}}>Save alias?</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:20}}>
              Save name <strong style={{color:'var(--accent)'}}>"{aliasModal.name}"</strong> as alias?
              <br/>Future scans of this name will auto-map.
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={handleSaveAlias} style={{flex:1,background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'1px solid var(--accent-strong)',borderRadius:8,padding:'10px',fontSize:13,fontWeight:700,cursor:'pointer'}}>✓ Save</button>
              <button onClick={()=>setAliasModal(null)} style={{flex:1,background:'var(--border)',color:'var(--text-secondary)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:8,padding:'10px',fontSize:13,cursor:'pointer'}}>Skip</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  topbar: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 },
  pageLabel: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:4 },
  pageTitle: { fontSize:22, fontWeight:600, color:'var(--text-primary)', letterSpacing:-0.3 },
  badge: { background:'var(--border)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:20, padding:'5px 12px', fontSize:12, color:'var(--text-secondary)' },
  kgBadge: { display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.22)', borderRadius:8 },
  kgLabel: { fontSize:10, color:'var(--text-muted)', fontWeight:600, letterSpacing:1, textTransform:'uppercase' },
  kgVal: { fontVariantNumeric:"tabular-nums",fontWeight:600, fontWeight:700, fontSize:14, color:'var(--accent)' },
  canBadge: { display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(124,58,237,0.06)', border:'1px solid rgba(124,58,237,0.22)', borderRadius:8 },
  canLabel: { fontSize:10, color:'var(--text-muted)', fontWeight:600, letterSpacing:1, textTransform:'uppercase' },
  canVal: { fontVariantNumeric:"tabular-nums",fontWeight:600, fontWeight:700, fontSize:14, color:'var(--color-purple)' },
  tableWrap: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12, overflow:'visible' },
  th: { background:'var(--bg-tertiary)', padding:'10px 12px', fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, textAlign:'left', borderBottom:'1px solid rgba(15,23,42,0.07)' },
  td: { padding:'10px 12px', fontSize:13, color:'var(--text-primary)' },
  empty: { padding:0 },
  emptyInner: { display:'flex', flexDirection:'column', alignItems:'center', padding:'52px 20px' },
};
